import { formatAgo, plural } from '../../../../shared/sso-workspace/format'

// Shared by the list, the phone cards and the detail.
export const STATUS_TONE = { open: 'bad', resolved: 'ok', ignored: null }
export const STATUS_LABEL = { open: 'Open', resolved: 'Resolved', ignored: 'Ignored' }
export const SOURCE_LABEL = { request: 'Request', job: 'Job', boot: 'Boot', client: 'Browser' }

// Where one sample, or most of an error's samples, came from (content's
// "from": the app of the request, read from the request log; a job;
// start-up), as a line: "From Mac app 1.5.0 · macOS", "From unknown
// (request no longer kept)", "Job: organization-purge", "Server start-up".
export const fromLine = from => {
  if (!from || !from.display) return ''
  if (from.kind === 'job' || from.kind === 'boot') return from.display
  // a browser error whose report the request log no longer keeps
  if (from.kind === 'client' && !from.app) return from.display
  if (from.kind === 'unknown') return `From ${from.display.charAt(0).toLowerCase()}${from.display.slice(1)}`
  return `From ${from.display}`
}

// A breakdown line's share of the samples read, as a whole percent ("<1%"
// for a sliver, never "0%" for a line that has samples).
export const sharePercent = (count, total) => {
  const n = Number(count) || 0
  const t = Number(total) || 0
  if (!t || !n) return '0%'
  const p = (n / t) * 100
  return p < 1 ? '<1%' : `${Math.round(p)}%`
}

// Where an error happens: the request's method and route, the job's name,
// boot, or the browser (a JavaScript crash, source client).
export const where = g => (g.source === 'job' ? `job ${g.jobName}` : g.source === 'boot' ? 'boot' : g.source === 'client' ? 'browser' : `${g.method} ${g.route}`)

// A path split for middle truncation: everything up to the last "/" (which
// may be cut short) and the file or function after it (always shown).
export const splitPath = s => {
  const text = String(s || '')
  const i = text.lastIndexOf('/')
  return i < 0 ? ['', text] : [text.slice(0, i + 1), text.slice(i + 1)]
}

// The breadcrumb's name for one error: its type, cut in the middle when long.
export const crumbName = (type, max = 32) => {
  const t = String(type || '').trim()
  if (!t) return 'Error'
  if (t.length <= max) return t
  const keep = max - 1
  return `${t.slice(0, Math.ceil(keep / 2))}…${t.slice(t.length - Math.floor(keep / 2))}`
}

// Generic error types core errortrack folds into one bucket (rapid-api-core
// ddd/common/errortrack/errtype.go: IsGeneric + genericTypes) name nothing on
// their own -- every plain errors.New / fmt.Errorf group would otherwise show
// the same title. The server always sends the collapsed "error" today; the Go
// type names are kept here defensively, for a row written before that
// normalization, or a future fingerprint version, that could still carry one
// of them through.
const GENERIC_TYPES = new Set(['error', '*errors.errorString', '*fmt.wrapError', '*fmt.wrapErrors', '*errors.joinError', 'apierror.ApiError', '*apierror.ApiError'])
export const isGenericType = type => GENERIC_TYPES.has(String(type || '').trim())

const MAX_TITLE = 80
const MIN_TITLE_HEAD = Math.floor(MAX_TITLE * 0.4)

// A short, readable title built from a message: its first sentence when it
// has one short enough to matter, else the whole thing -- flattened to one
// line, trimmed, and ellipsis-cut to about 80 characters at a word boundary
// (never mid-word).
export const shortMessage = message => {
  const flat = String(message || '').replace(/\s+/g, ' ').trim()
  if (!flat) return ''
  const sentence = flat.match(/^.*?[.!?](?=\s|$)/)
  const head = sentence && sentence[0].length < flat.length ? sentence[0].trim() : flat
  if (head.length <= MAX_TITLE) return head
  const cut = head.slice(0, MAX_TITLE)
  const atSpace = cut.lastIndexOf(' ')
  return `${(atSpace > MIN_TITLE_HEAD ? cut.slice(0, atSpace) : cut).trimEnd()}…`
}

// A group's title, everywhere one shows (the list, the cards, the detail
// heading, the breadcrumb): its type, or -- when the type is generic and so
// reads as just "error" -- a short form of its message instead, so two
// unrelated bugs no longer look identical.
//
// A browser crash (source client) is named by what went wrong (owner
// 2026-10-16): JavaScript has few error types ("js:TypeError", "js:Error"
// when the name is missing), so the type alone made every crash read the
// same. Its title is the name and the message ("TypeError: Cannot read
// properties of undefined"), the message alone when the name is the plain
// "Error", and the type when there is no message (core stores "(no
// message)" for an empty one).
export const groupTitle = g => {
  if (!g) return 'Error'
  if (isGenericType(g.errorType)) return shortMessage(g.sampleMessage) || 'Error'
  if (g.source === 'client') return clientTitle(g)
  return g.errorType || 'Error'
}

const clientTitle = g => {
  const raw = String(g.sampleMessage || '').trim()
  const message = raw === '(no message)' ? '' : shortMessage(raw)
  if (!message) return g.errorType || 'Error'
  const name = String(g.errorType || '').replace(/^js:/, '').trim()
  return name && name !== 'Error' ? `${name}: ${message}` : message
}

// Who last changed the status, and when, in a sentence ('' when nobody has).
// A resolved error that happens again reopens itself (came_back_at).
export const statusLine = (g, now = Date.now()) => {
  const by = g.statusChangedBy ? ` by ${g.statusChangedBy}` : ''
  const at = g.statusChangedAt ? formatAgo(g.statusChangedAt, now) : ''
  if (g.status === 'resolved' && at) return `Resolved ${at}${by}`
  if (g.status === 'ignored' && at) return `Ignored ${at}${by}`
  if (g.status === 'open' && g.cameBackAt && !g.statusChangedBy) return `Reopened by itself ${formatAgo(g.cameBackAt, now)}: it happened again after it was resolved`
  if (g.status === 'open' && at && g.statusChangedBy) return `Reopened ${at}${by}`
  return ''
}

// came_back_count: how many times the error happened again after it was
// resolved (each time it reopened itself; rapid-api-core errortrack).
export const recurredText = n => (Number(n) > 0 ? plural(n, 'time') : 'Never')

// The activity chart's windows. step is what the buckets route takes (5m,
// 1h, 1d over `days` days); hours trims the answer to the window's length.
const H = 3600e3
export const RANGES = [
  { key: '6h', label: '6 hours', short: '6h', days: 1, step: '5m', hours: 6 },
  { key: '24h', label: '24 hours', short: '24h', days: 1, step: '1h', hours: 24 },
  { key: '7d', label: '7 days', short: '7d', days: 7, step: '1h', hours: 7 * 24 },
  { key: '30d', label: '30 days', short: '30d', days: 30, step: '1d', hours: 30 * 24 },
  { key: '90d', label: '90 days', short: '90d', days: 90, step: '1d', hours: 90 * 24 }
]
export const STEP_MS = { '5m': 300e3, '1h': H, '1d': 24 * H }

// The smallest window that holds the error's whole life (first seen to
// now), so its bars sit inside the chart instead of one bar at the edge.
export const pickRange = (g, now = Date.now()) => {
  const first = Date.parse(g && g.firstSeenAt)
  const age = Number.isFinite(first) ? (now - first) / H : Infinity
  return (RANGES.find(r => age <= r.hours * 0.9) || RANGES[RANGES.length - 1]).key
}

// The route's buckets ([{ at, count }]) within the window, as [ms, count].
export const windowed = (buckets, range, now = Date.now()) => {
  const from = now - range.hours * H
  return (Array.isArray(buckets) ? buckets : [])
    .map(b => [Date.parse(b.at), Number(b.count) || 0])
    .filter(([t]) => Number.isFinite(t) && t + STEP_MS[range.step] > from)
}
